import { div, h1, h2, p, span, button, img, a, fnstate, goTo } from '@srfnstack/fntags'

// Deliberately spartan one-tap approval queue. Products autopilot generated,
// quality-gated, and pushed to Printify sit here awaiting a single Publish tap.
export const AutopilotQueue = () => {
  const queue = fnstate([])
  const loading = fnstate(true)
  const busy = fnstate(null)
  // An empty queue has several very different causes and they are not
  // interchangeable — autopilot switched off is a broken shop, artwork waiting on
  // a decision is a nudge. Pull the upstream state so the empty view can say which.
  const progress = fnstate(null)

  const load = async () => {
    loading(true)
    try {
      const res = await fetch('/api/system/autopilot/queue')
      queue(res.ok ? await res.json() : [])
    } catch { queue([]) }
    try {
      const res = await fetch('/api/system/autopilot/progress')
      progress(res.ok ? await res.json() : null)
    } catch { progress(null) }
    loading(false)
  }

  const act = async (id, action) => {
    busy(id)
    try {
      const res = await fetch(`/api/system/autopilot/queue/${id}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}'
      })
      if (res.ok) {
        queue(queue().filter(x => x.id !== id))
      } else {
        const e = await res.json().catch(() => ({}))
        window.alert(`Failed to ${action}: ${e.error || 'unknown error'}`)
      }
    } catch (err) {
      window.alert(`Failed to ${action}: ${err.message}`)
    }
    busy(null)
  }

  const scoreBadge = (label, val) => (val === null || val === undefined)
    ? ''
    : span({ class: 'badge badge-success badge-sm' }, `${label} ${Number(val).toFixed(1)}`)

  const openProduct = (item) => goTo(`/product-review/${item.id}?from=/approval-queue`)

  // Why is this empty? Most urgent explanation first.
  const emptyState = (pg) => {
    if (pg && pg.autopilot_enabled === false) {
      return div({ class: 'text-center p-12' },
        div({ class: 'text-warning font-semibold mb-2' }, 'Autopilot is switched OFF.'),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Nothing is being generated, so nothing will reach this queue. Products already finished are picked back up automatically once you turn it on.'),
        button({ class: 'btn btn-primary btn-sm', onclick: () => goTo('/settings') }, 'Open Autopilot settings'))
    }
    if (pg && pg.awaiting_artwork_approval > 0) {
      return div({ class: 'text-center p-12' },
        div({ class: 'font-semibold mb-2' }, `${pg.awaiting_artwork_approval} design${pg.awaiting_artwork_approval === 1 ? '' : 's'} waiting on artwork approval.`),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Autopilot pauses generation while the artwork queue is full. Approve or reject them and the pipeline refills.'),
        button({ class: 'btn btn-primary btn-sm', onclick: () => goTo('/artwork-approval') }, 'Review artwork'))
    }
    if (pg && pg.push_failed > 0) {
      return div({ class: 'text-center p-12' },
        div({ class: 'font-semibold mb-2' }, `${pg.push_failed} product${pg.push_failed === 1 ? '' : 's'} cleared the bar — the push to Printify is failing.`),
        p({ class: 'text-sm opacity-70 mb-2' },
          'Autopilot retries the push every tick, so nothing is lost. This is a Printify-side error until it says otherwise:'),
        p({ class: 'text-xs font-mono opacity-60 mb-4 break-all' }, pg.push_failed_reason || '(no error text recorded)'),
        button({ class: 'btn btn-sm', onclick: () => goTo('/queue') }, 'Open Queue Manager'))
    }
    if (pg && pg.held_pre_push > 0) {
      return div({ class: 'text-center p-12' },
        div({ class: 'font-semibold mb-2' }, `${pg.held_pre_push} product${pg.held_pre_push === 1 ? '' : 's'} held before Printify.`),
        p({ class: 'text-sm opacity-70' },
          'These finished mockups but did not clear the bar to auto-push. Open Product Review to publish them by hand.'),
        button({ class: 'btn btn-sm mt-4', onclick: () => goTo('/product-review') }, 'Open Product Review'))
    }
    return div({ class: 'text-center opacity-60 p-12' }, 'Nothing waiting for approval right now.')
  }

  const card = (item) => div({ class: 'card bg-base-200 border border-base-300 p-4' },
    div({
      class: 'grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 cursor-pointer',
      title: 'Open product concept',
      onclick: () => openProduct(item)
    },
    (item.mockups || []).slice(0, 4).map(m => img({
      src: `/api/image?file=${encodeURIComponent(m.file_path)}`,
      class: 'w-full aspect-square object-cover rounded bg-base-100',
      loading: 'lazy',
      decoding: 'async'
    }))
    ),
    h2({ class: 'font-semibold text-sm mb-2 cursor-pointer link link-hover', onclick: () => openProduct(item) }, item.title || `Product #${item.id}`),
    div({ class: 'flex flex-wrap items-center gap-2 mb-4 text-xs' },
      span({ class: 'badge badge-ghost badge-sm' }, item.product_type || 'product'),
      scoreBadge('mockup', item.mockup_quality_score),
      scoreBadge('art', item.quality_score),
      item.printify_product_id
        ? a({ href: `https://printify.com/app/products/${item.printify_product_id}`, target: '_blank', rel: 'noopener', class: 'link link-primary ml-auto' }, 'Open in Printify ↗')
        : ''
    ),
    div({ class: 'flex gap-2' },
      button({
        class: 'btn btn-success flex-1',
        disabled: busy.bindAttr(b => b === item.id),
        onclick: () => act(item.id, 'approve')
      }, busy.bindAs(b => b === item.id ? span({ class: 'loading loading-spinner loading-sm' }) : 'Publish to Etsy')),
      button({
        class: 'btn btn-ghost',
        disabled: busy.bindAttr(b => b === item.id),
        onclick: () => act(item.id, 'reject')
      }, 'Reject')
    )
  )

  load()

  return div({ class: 'p-6' },
    h1({ class: 'text-3xl font-bold mb-1' }, 'Approval Queue'),
    p({ class: 'text-sm opacity-70 mb-6' },
      'Autopilot generated and quality-checked these products and pushed them to Printify. One tap publishes to Etsy.'),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : queue.bindAs(list => (list.length === 0)
        ? progress.bindAs(emptyState)
        : div({ class: 'grid gap-4 max-w-3xl' }, list.map(card))))
  )
}
